import * as React from 'react';
import { useSelector } from 'react-redux';
import { CheckoutPage } from 'pages/billing/checkout-page';
import { BillingPage } from 'pages/billing';
import { PricingPage } from 'pages/billing/pricing-page';
import { VIESPage } from 'pages/vies';
import { CustomersSuppliersPage } from 'pages/vies/customers-suppliers';
import { ContactPage } from 'pages/vies/contact';
import { NotIncludedPage } from 'pages/not-included';
import { VatCheckerUnavailablePage } from 'pages/vies/temporarily-unavailable';
import { LicenseStatus, LicenseType } from 'store/app/enums';
import { getAuthUserLicence } from 'store/auth/selectors';
import { isVatCheckerDisabled } from 'utils/feature-flag-utils';

type PageComponent = React.ComponentType<Record<string, unknown>>;

export const resolveLicencedComponent = (
  defaultComponent: PageComponent,
  userLicence: ReturnType<typeof getAuthUserLicence>
): PageComponent => {
  if (
    isVatCheckerDisabled() &&
    (defaultComponent === VIESPage ||
      defaultComponent === CustomersSuppliersPage ||
      defaultComponent === ContactPage)
  ) {
    return VatCheckerUnavailablePage;
  }

  if (
    (userLicence?.licenseType === LicenseType.TRIAL &&
      userLicence?.licenseDaysLeft === 0 &&
      defaultComponent !== CheckoutPage) ||
    (userLicence?.licenseType === LicenseType.TRIAL &&
      defaultComponent === BillingPage)
  ) {
    return PricingPage;
  }

  if (
    userLicence?.licenseType === LicenseType.TRIAL &&
    userLicence?.status !== LicenseStatus.UNLIMITED &&
    defaultComponent === VIESPage
  ) {
    return NotIncludedPage;
  }

  if (
    userLicence?.licenseType === LicenseType.BLOCKED &&
    userLicence?.licenceExpireDate &&
    new Date(userLicence.licenceExpireDate) < new Date()
  ) {
    return BillingPage;
  }

  return defaultComponent;
};

export const LicencedPage: React.FC<{ component: PageComponent }> = ({
  component,
}) => {
  const userLicence = useSelector(getAuthUserLicence);
  const Page = React.useMemo(
    () => resolveLicencedComponent(component, userLicence),
    [component, userLicence]
  );

  return <Page />;
};
